import React, { useRef, useState, useCallback } from "react";
import AddIcon from "@mui/icons-material/Add";
import { uploadImage } from "../../http/uploads";
import { EditOutlined, VisibilityOutlined } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { setLoading } from "../../store/slices/common";
import { ToastContainer, toast } from "react-toastify";
import ImageCropper from "../ImageCropper";

const UploadBadge = ({
  onImageUpload,
  selectedBadge,
  view,
  audio,
  karaoke,
}) => {
  const [image, setImage] = useState(
    selectedBadge?.logo || selectedBadge?.image || null
  );
  const [cropperOpen, setCropperOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();

  // const handleImageChange = async (event) => {
  //     const files = event.target.files
  //     if (files.length > 1) {
  //         toast.error('Only one image can be added at a time.');
  //         return;
  //     }
  //     const MAX_FILE_SIZE = karaoke ? 1024 * 1024 : 5 * 1024 * 1024;
  //     const file = event.target.files[0];
  //     if (!file) return;
  //     if (file) {
  //         const validFormats = ['image/jpeg', 'image/png'];
  //         if (!validFormats.includes(file.type) && karaoke) {
  //             toast.error('Invalid file format. Please upload a JPG or PNG image.');
  //             return;
  //         }
  //         if (file.size > MAX_FILE_SIZE) {
  //             karaoke
  //                 ? toast.error('File size exceeds 1MB limit. Please upload a smaller file.')
  //                 : toast.error('File size exceeds 5MB limit. Please upload a smaller file.');
  //             return;
  //         }

  //         const img = new Image();
  //         // img.onload = () => {
  //         const aspectRatio = img.width / img.height;
  //         if (karaoke && aspectRatio !== 1) {
  //             toast.error('Image must have an aspect ratio of 1:1.');
  //             return;
  //         }
  //         // }

  //         const formData = new FormData();
  //         formData.append('file', file);
  //         formData.append('folder', 'badge')
  //         try {
  //             dispatch(setLoading(true))
  //             const res = await uploadImage(formData);
  //             setImage(res?.data?.data?.url)
  //             audio ? onImageUpload(res?.data?.data?._id) : karaoke ? onImageUpload(res?.data?.data) : onImageUpload(res?.data?.data?.url)
  //         } catch (error) {
  //             console.error(error);
  //         } finally {
  //             dispatch(setLoading(false))

  //         }
  //     }
  // };

  // Handle click to trigger file input

  const processFile = useCallback(async (file) => {
    if (!file) return;

    const validFormats = ["image/jpeg", "image/png"];
    if (!validFormats.includes(file.type) && karaoke) {
      toast.error("Invalid file format. Please upload a JPG or PNG image.");
      return;
    }

    if (karaoke) {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        const minDimension = Math.min(img.width, img.height);
        if (minDimension < 256) {
          toast.warning("Image is very small (" + minDimension + "px). The result may appear pixelated.");
        }
        setImageToCrop(objectUrl);
        setCropperOpen(true);
      };

      img.onerror = () => {
        toast.error("Failed to load image. Please try again.");
        URL.revokeObjectURL(objectUrl);
      };

      img.src = objectUrl;
    } else {
      const MAX_FILE_SIZE = 5 * 1024 * 1024;
      if (file.size > MAX_FILE_SIZE) {
        toast.error("File size exceeds 5MB limit. Please upload a smaller file.");
        return;
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", "badge");

      try {
        dispatch(setLoading(true));
        const res = await uploadImage(formData);
        setImage(res?.data?.data?.url);
        audio
          ? onImageUpload(res?.data?.data?._id)
          : onImageUpload(res?.data?.data?.url);
      } catch (error) {
        console.error(error);
      } finally {
        dispatch(setLoading(false));
      }
    }
  }, [karaoke, audio, dispatch, onImageUpload]);

  const handleImageChange = async (event) => {
    const files = event.target.files;
    if (files.length > 1) {
      toast.error("Only one image can be added at a time.");
      return;
    }
    await processFile(files[0]);
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    if (files.length > 1) {
      toast.error("Only one image can be added at a time.");
      return;
    }

    const file = files[0];
    if (!file.type.startsWith("image/")) {
      toast.error("Please drop an image file.");
      return;
    }

    await processFile(file);
  }, [processFile]);

  // Handle cropped image from the cropper
  const handleCroppedImage = async (croppedFile) => {
    // Check file size after cropping (should be under 1MB for karaoke)
    if (croppedFile.size > 1024 * 1024) {
      toast.error("Cropped image exceeds 1MB. Try zooming out or using a smaller image.");
      return;
    }

    const formData = new FormData();
    formData.append("file", croppedFile);
    formData.append("folder", karaoke ? "karaoke" : "badge");
    if (karaoke) {
      formData.append("metadata", JSON.stringify({ type: "image" }));
    }

    try {
      dispatch(setLoading(true));
      const res = await uploadImage(formData);
      setImage(res?.data?.data?.url);
      onImageUpload(res?.data?.data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to upload image. Please try again.");
    } finally {
      dispatch(setLoading(false));
      // Clean up
      if (imageToCrop) {
        URL.revokeObjectURL(imageToCrop);
        setImageToCrop(null);
      }
    }
  };

  const handleCropperClose = () => {
    setCropperOpen(false);
    if (imageToCrop) {
      URL.revokeObjectURL(imageToCrop);
      setImageToCrop(null);
    }
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleAddClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const previewHandler = () => {
    if (image) {
      window.open(image, "_blank");
    }
  };

  return (
    <div className="custom-crop-upload">
      <input
        type="file"
        multiple
        onChange={(e) => handleImageChange(e)}
        style={{ display: "none" }}
        accept="image/*"
        ref={fileInputRef}
      />
      {!image && (
        <div
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleAddClick}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            border: isDragging ? "2px dashed #0832DE" : "1px dashed",
            height: "100px",
            width: "100px",
            borderRadius: "16px",
            cursor: "pointer",
            background: isDragging ? "rgba(8, 50, 222, 0.06)" : "transparent",
            transition: "all 0.2s ease",
          }}
        >
          <AddIcon
            sx={{ color: isDragging ? "#0832DE" : "#142c5c" }}
          />
          <span
            style={{
              fontWeight: "600",
              fontSize: "11px",
              color: isDragging ? "#0832DE" : "#142c5c",
              textAlign: "center",
              lineHeight: "1.3",
              padding: "0 4px",
            }}
          >
            {isDragging
              ? "Drop here"
              : audio
              ? "Thumbnail"
              : karaoke
              ? "Album cover"
              : "Upload Badge"}
          </span>
        </div>
      )}
      {image && (
        <div
          style={{ position: "relative", width: "maxContent" }}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <img
            src={image}
            alt="Uploaded"
            className="badge-image"
            style={{
              width: "100px",
              height: "100px",
              objectFit: "cover",
              borderRadius: "16px",
              border: isDragging ? "2px solid #0832DE" : "1px solid grey",
              opacity: isDragging ? 0.6 : 1,
              transition: "all 0.2s ease",
            }}
          />
          {isDragging && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "16px",
                background: "rgba(8, 50, 222, 0.12)",
                pointerEvents: "none",
              }}
            >
              <span style={{ color: "#0832DE", fontWeight: 600, fontSize: "11px" }}>
                Drop to replace
              </span>
            </div>
          )}
          {!view && !isDragging && (
            <div className="delete-button">
              <div
                onClick={previewHandler}
                style={{
                  border: "1px solid gray",
                  padding: "5px 5px 2px 5px",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                <VisibilityOutlined sx={{ color: "gray", fontSize: "18px" }} />
              </div>
              <div
                onClick={handleAddClick}
                style={{
                  border: "1px solid gray",
                  padding: "5px 5px 2px 5px",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                <EditOutlined sx={{ color: "gray", fontSize: "18px" }} />
              </div>
            </div>
          )}
        </div>
      )}
      <ToastContainer />

      <ImageCropper
        open={cropperOpen}
        onClose={handleCropperClose}
        imageSrc={imageToCrop}
        onCropComplete={handleCroppedImage}
        aspectRatio={1}
        outputSize={512}
        title="Crop Album Cover"
      />
    </div>
  );
};

export default UploadBadge;
